/**
 * The parts of the design that have no backend.
 *
 * `docs/design/reference.html` shows twelve screens. Catalog, product and cart
 * are real: they run on commerce-scs' GraphQL through `@commerce/core`.
 * Everything in this file — distributors, stories, delivery windows, order
 * history — has **no endpoint in the ported domain**,
 * which was audited in Task 4. Nothing here is a mock standing in for something
 * that exists; it is a placeholder for something that does not.
 *
 * That distinction has to survive into the demo. Say "this screen is wired to
 * the real cart" and "this screen is a design running on local state", because
 * someone will ask.
 *
 * Values are transcribed from the design so the screens match it exactly.
 */

export interface Distributor {
  id: string
  name: string
  /** Two-letter monogram the design draws when there is no logo. */
  initials: string
  /** Pale background behind the monogram. */
  tint: string
  /** ISO weekdays this distributor delivers on (1 = Monday). */
  deliveryDays: number[]
  deliveryDaysLabel: string
  meta: string
  /** Credit available to this store, in the storefront's currency. */
  credit: number
  creditLine: number
  /** The day the sales rep visits. */
  visitDay: string
}

export const DISTRIBUTORS: readonly Distributor[] = [
  {
    id: 'd1',
    name: 'Distribuidora Central Norte',
    initials: 'CN',
    tint: '#E2F1F7',
    deliveryDays: [2, 4],
    deliveryDaysLabel: 'martes y jueves',
    meta: '128 SKUs · línea de crédito $8,500',
    credit: 8500,
    creditLine: 12000,
    visitDay: 'Jueves',
  },
  {
    id: 'd2',
    name: 'Comercial Bajío',
    initials: 'CB',
    tint: '#FBEFDD',
    deliveryDays: [3, 5],
    deliveryDaysLabel: 'miércoles y viernes',
    meta: '96 SKUs · línea de crédito $5,000',
    credit: 5000,
    creditLine: 8000,
    visitDay: 'Viernes',
  },
]

/**
 * What a story's call to action does. The screens map these to navigation.
 *
 * `browse` is the real-promotion case: a storefront-wide offer has nowhere
 * specific to send the shopper, so its CTA just closes the reel and leaves
 * them on the catalog to spend against it.
 */
export type StoryAction = 'addSuggested' | 'payment' | 'category' | 'visit' | 'browse'

export interface Story {
  key: string
  label: string
  initials: string
  tint: string
  /** Foreground for the monogram when the tint is dark. */
  fg?: string
  eyebrow: string
  title: string
  body: string
  ctaLabel: string
  action: StoryAction
  /** For `category` actions: which category to open. */
  categoryName?: string
}

export const STORIES: readonly Story[] = [
  {
    key: 's1',
    label: '2×1 en Trident',
    initials: '2×1',
    tint: '#E7F3EA',
    eyebrow: 'Promoción de la semana',
    title: '2×1',
    body: 'en tu segunda caja de Trident 4s Menta. Solo esta semana.',
    ctaLabel: 'Agregar al carrito',
    action: 'addSuggested',
  },
  {
    key: 's2',
    label: 'Tu crédito listo',
    initials: '$',
    tint: '#0E151C',
    fg: '#40E4A9',
    eyebrow: 'Crédito disponible',
    title: '$8,500',
    body: 'listos para tu pedido de hoy. Sin trámites, se descuenta al pagar.',
    ctaLabel: 'Ver métodos de pago',
    action: 'payment',
  },
  {
    key: 's3',
    label: 'Nuevos SKUs',
    initials: '+3',
    tint: '#EEE9F7',
    eyebrow: 'Nuevos en catálogo',
    title: '+3',
    body: 'SKUs de temporada en Dulces. Sé el primero en tu zona.',
    ctaLabel: 'Ver dulces',
    action: 'category',
    categoryName: 'Dulces',
  },
  {
    key: 's4',
    label: 'Visita el jueves',
    initials: 'Ju',
    tint: '#FBEFDD',
    eyebrow: 'Ruta de tu asesora',
    title: 'Jue',
    body: 'Laura visita tu tienda este jueves 13. Confirma tu día.',
    ctaLabel: 'Confirmar día de visita',
    action: 'visit',
  },
]

export interface ShippingMethod {
  id: 'programada' | 'express' | 'pickup'
  label: string
  detail: string
  /** Added to the order total. The express fee is configurable in the design. */
  fee: number
  /** Pickup needs no delivery date. */
  needsDate: boolean
}

export const shippingMethods = (expressFee = 60): readonly ShippingMethod[] => [
  {
    id: 'programada',
    label: 'Entrega programada',
    detail: 'En el día de reparto de tu zona',
    fee: 0,
    needsDate: true,
  },
  {
    id: 'express',
    label: 'Express 24 horas',
    detail: 'Llega mañana antes de las 14:00',
    fee: expressFee,
    needsDate: true,
  },
  {
    id: 'pickup',
    label: 'Recoger en distribuidor',
    detail: 'Listo en 3 horas en Parque Norte',
    fee: 0,
    needsDate: false,
  },
]

export type OrderStatus = 'Confirmado' | 'Entregado' | 'Cancelado'

export interface OrderLine {
  name: string
  quantity: number
  amount: number
}

export interface Order {
  /**
   * The real order's id, which is what the detail route is built from. A Mongo
   * ObjectId — the server rejects anything else with "'id' must only contain
   * hexadecimal characters", so this can never be a locally minted string.
   */
  id: string
  /** What a person reads as the order number; `id` is unreadable. */
  reference?: string
  date: string
  total: number
  status: OrderStatus
  count: number
  delivery: string
  payment: string
  lines: OrderLine[]
  /** sku -> quantity, so "repetir pedido" can refill a real cart. */
  reorder: Record<string, number>
}

/**
 * There is no seeded order history any more: Pedidos and the order detail read
 * the session's real orders from `getOrders` / `order`. What remains here is
 * the shape `placeOrder` records locally so the confirmation screen can name
 * the order that was just placed, which no endpoint reports back.
 */
export const ORDER_TABS = ['Todos', 'Confirmados', 'Entregados', 'Cancelados'] as const
export type OrderTab = (typeof ORDER_TABS)[number]

export const WEEKDAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'] as const
